<template>
  <div
    :role="role"
    :aria-live="live"
    aria-atomic="true"
    :class="classes"
    @mouseenter="$emit('pause', toast.id)"
    @mouseleave="$emit('resume', toast.id)"
    @focusin="$emit('pause', toast.id)"
    @focusout="$emit('resume', toast.id)"
  >
    <span class="mt-0.5 flex-shrink-0">
      <BaseIcon :name="TONE_ICON[toast.tone]" :size="20" />
    </span>
    <div class="min-w-0 flex-1 text-ink">
      <p v-if="toast.title" class="text-sm font-semibold text-ink-strong">{{ toast.title }}</p>
      <p class="text-sm leading-5 break-words">{{ toast.message }}</p>
      <button
        v-if="toast.action"
        type="button"
        class="mt-2 text-sm font-semibold text-accent underline-offset-4 hover:underline focus-visible:outline-none focus-visible:shadow-[var(--shadow-ring-focus)] rounded"
        @click="$emit('action', toast.id)"
      >
        {{ toast.action.label }}
      </button>
    </div>
    <BaseIconButton
      size="sm"
      variant="ghost"
      :label="t('aria.dismissToast')"
      class="-mr-1 -mt-1"
      @click="$emit('dismiss', toast.id)"
    >
      <BaseIcon name="close" :size="16" />
    </BaseIconButton>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { cva } from 'class-variance-authority';
import BaseIcon from '@/components/base/BaseIcon.vue';
import BaseIconButton from '@/components/base/BaseIconButton.vue';
import type { ToastItem, ToastTone } from '@/composables/useToast';
import type { IconName } from '@/components/base/icons';

const toastVariants = cva(
  [
    'pointer-events-auto flex items-start gap-3 rounded-md border p-4 shadow-lg',
    'min-w-[280px] max-w-[420px] bg-paper',
  ],
  {
    variants: {
      tone: {
        info: 'bg-info-100 border-info-500 text-info-500',
        success: 'bg-success-50 border-success-500 text-success-700',
        warning: 'bg-warning-100 border-warning-500 text-warning-700',
        danger: 'bg-danger-100 border-danger-500 text-danger-700',
      },
    },
    defaultVariants: { tone: 'info' },
  },
);

interface Props {
  toast: ToastItem;
}

const props = defineProps<Props>();

defineEmits<{
  (e: 'dismiss', id: string): void;
  (e: 'pause', id: string): void;
  (e: 'resume', id: string): void;
  (e: 'action', id: string): void;
}>();

const { t } = useI18n();

const TONE_ICON: Record<ToastTone, IconName> = {
  info: 'info',
  success: 'success',
  warning: 'warning',
  danger: 'danger',
};

const classes = computed(() => toastVariants({ tone: props.toast.tone }));
const role = computed(() => (props.toast.tone === 'danger' ? 'alert' : 'status'));
const live = computed(() => (props.toast.tone === 'danger' ? 'assertive' : 'polite'));
</script>
